import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import {
  Typography,
  Box,
  Paper,
  Chip,
  Button,
  LinearProgress,
  Alert,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { RichTextDisplay, QuestionRenderer, DialogueGroupExerciseRenderer, PreviewAudioButton } from "@/components/common";
import QuestionImage from "../../ExamSession/QuestionImage";
import SimplePartInstruction from "./SimplePartInstruction";
import { delay } from "@/utils/common";

/**
 * LC Exercise Session Component
 * Handles Listening Comprehension exercises with automatic audio playback
 *
 * Features:
 * - Auto-plays audio when question loads
 * - Manual navigation for part instructions
 * - Reusable for attendance tests
 */
const LCExerciseSession = ({
  parts,
  currentPartIndex,
  currentGroupIndex,
  showInstructions,
  answers,
  sectionInfo,
  dayLabel,
  onAnswerSelect,
  onNext,
  onPrevious,
  getTotalQuestions,
  getAnsweredQuestions,
  seenPartInstructions,
}) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasUserInteracted] = useState(true); // Set to true immediately for exercises
  const [hasPlayedOnce, setHasPlayedOnce] = useState(false); // Track if audio has played successfully once
  const isMobile = /Android|webOS|iPhone|iPad|iPod/i.test(navigator.userAgent);

  // Check if this is the very first question (first part, first group, not showing instructions)
  const isFirstQuestion = currentPartIndex === 0 && currentGroupIndex === 0 && !showInstructions;

  const currentPart = useMemo(
    () => parts?.[currentPartIndex] || null,
    [parts, currentPartIndex]
  );

  const questionGroup = useMemo(() => {
    return currentPart?.questionGroups || [];
  }, [currentPart]);

  const currentGroup = useMemo(
    () => questionGroup?.[currentGroupIndex] || null,
    [questionGroup, currentGroupIndex]
  );

  const currentGroupType = useMemo(() => {
    return currentGroup?.questions?.[0]?.type || null;
  }, [currentGroup]);


  // Play audio function
  const playAudio = useCallback(async (src) => {
    try {
      if (!audioRef.current) {
        console.warn("Audio reference not available");
        await new Promise((resolve) => setTimeout(resolve, 500));
        if (!audioRef.current) {
          console.error("Audio element still not available");
          return false;
        }
      }

      // Pause current audio before changing source
      audioRef.current.pause();
      audioRef.current.volume = 1;

      if (!src) {
        console.error("No audio source provided");
        return false;
      }

      console.log("Setting audio source:", src);
      audioRef.current.src = src;

      // Speed up for development
      if (process.env.NODE_ENV === "development") {
        audioRef.current.playbackRate = 2;
      }

      // Small delay to ensure audio source is loaded
      await new Promise((resolve) => setTimeout(resolve, 100));

      console.log("Playing audio");
      await audioRef.current.play();
      return true;
    } catch (error) {
      console.error("Error playing audio:", error);
      toast.error("Failed to play audio");
      return false;
    }
  }, []);

  // Auto-play audio for questions only (not for instructions)
  useEffect(() => {
    if (!hasUserInteracted) {
      console.log("Waiting for user interaction to play audio");
      return;
    }

    // Pause any existing audio
    if (audioRef.current) {
      audioRef.current.pause();
    }

    // Only play question group audio, not instruction audio
    if (!showInstructions && currentGroup?.audioUrl) {
      console.log("Playing question audio:", currentGroup.audioUrl);
      (async () => {
        await delay(1000);
        playAudio(currentGroup.audioUrl);
      })();
    }
  }, [
    currentGroup,
    showInstructions,
    hasUserInteracted,
    playAudio,
  ]);

  // Handle audio ended
  const handleAudioEnded = useCallback(() => {
    console.log("Audio ended", currentGroupIndex, questionGroup);
    setIsPlaying(false);
  }, [currentGroupIndex, questionGroup]);

  // Handle audio play
  const handleAudioPlay = useCallback(() => {
    setIsPlaying(true);
    setHasPlayedOnce(true); // Mark that audio has played successfully
  }, []);

  // Initialize audio ref check (similar to ExamSession)
  useEffect(() => {
    const checkAudioRef = () => {
      if (!audioRef.current && hasUserInteracted) {
        setTimeout(checkAudioRef, 100);
      }
    };
    checkAudioRef();
  }, [hasUserInteracted]);

  return (
    <Box sx={{ p: 0 }}>
      {/* Mobile audio instruction - only show on first question if audio hasn't played yet */}
      {isMobile && isFirstQuestion && !hasPlayedOnce && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Please click play to start the audio. This action only needs to be done once.
        </Alert>
      )}

      {/* Audio element - show controls on mobile only for first question */}
      {/* <Box
        sx={{
          justifyContent: "center",
          display: "flex",
          width: "100%",
          mb: 2,
        }}
      >
        <audio
          ref={audioRef}
          onEnded={handleAudioEnded}
          onPlay={handleAudioPlay}
          onPause={() => setIsPlaying(false)}
          preload="auto"
          controls={isMobile && isFirstQuestion && !hasPlayedOnce}
          style={
            !isMobile || hasPlayedOnce || !isFirstQuestion
              ? { display: "none" }
              : {
                  display: "block",
                  width: "50px",
                  height: "40px",
                }
          }
        />
      </Box> */}

      {/* Show part instructions */}
      {showInstructions &&
      currentPart &&
      !seenPartInstructions?.includes(currentPart.partNumber) ? (
        <SimplePartInstruction
          currentPart={currentPart}
          partStyle={{ p: 0, boxShadow: "none" }}
          onNext={onNext}
          onPrevious={onPrevious}
          isFirstPart={currentPartIndex === 0 && currentGroupIndex === 0}
        />
      ) : (
        <>
          {/* Header with Progress */}
          <Paper
            sx={{
              p: 3,
              mb: 3,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Box>
                <Typography variant="h5" fontWeight="bold">
                  {sectionInfo?.title || "Listening Exercise"}
                </Typography>
                {dayLabel && (
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {dayLabel}
                  </Typography>
                )}
              </Box>
              <Chip
                label={`${getAnsweredQuestions()} / ${getTotalQuestions()} Answered`}
                sx={{
                  bgcolor: "white",
                  color: "primary.main",
                  fontWeight: "bold",
                }}
              />
            </Box>
            <LinearProgress
              variant="determinate"
              value={(getAnsweredQuestions() / getTotalQuestions()) * 100}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: "rgba(255,255,255,0.3)",
                "& .MuiLinearProgress-bar": {
                  bgcolor: "white",
                  borderRadius: 4,
                },
              }}
            />
          </Paper>

          {/* Part and Question Info */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <Typography variant="h6" fontWeight="medium">
              Part {currentPart?.partNumber} - Question {currentGroupIndex + 1}{" "}
              of {questionGroup.length}
            </Typography>


          {/** Preview audio */}
          <PreviewAudioButton
            audioUrl={currentGroup?.audioUrl}
            onPlayAudio={playAudio}
            audioPlaying={isPlaying}
            isShowAllOptions={true}
            style={{width: "40%"}}
          />
          </Box>

          {/* Main Content */}
          {currentGroup?.content && currentGroupType !== "fill-blank" && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <RichTextDisplay content={currentGroup.content} />
            </Paper>
          )}

          {/* Image */}
          {currentGroup?.imageUrl && (
            <QuestionImage imageUrl={currentGroup.imageUrl} />
          )}

          {/* Questions - Check if dialogue group */}
          {currentGroupType === 'dialogue-group' ? (
            <DialogueGroupExerciseRenderer
              group={currentGroup}
              answers={answers}
              onAnswerChange={onAnswerSelect}
              showResult={false}
              disabled={false}
            />
          ) : (
            <Box>
              {currentGroup?.questions?.map((question, index) => (
                <QuestionRenderer
                  key={question._id}
                  question={question}
                  answer={answers[question._id]}
                  onAnswerChange={(answer) =>
                    onAnswerSelect(question._id, answer, question.type)
                  }
                  showResult={false}
                  disabled={false}
                  questionNumber={question.number || index + 1}
                />
              ))}
            </Box>
          )}

          {/* Navigation Buttons */}
          <Box display="flex" justifyContent="space-between" mt={4} gap={2}>
            <Button
              variant="outlined"
              onClick={onPrevious}
              disabled={currentPartIndex === 0 && currentGroupIndex === 0}
              size="large"
              startIcon={<ArrowBackIcon />}
            >
              Previous
            </Button>
            <Button
              variant="contained"
              onClick={onNext}
              size="large"
              endIcon={<ArrowForwardIcon />}
            >
              {currentPartIndex === parts.length - 1 &&
              currentGroupIndex === questionGroup.length - 1
                ? "Finish"
                : "Next"}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default LCExerciseSession;
